import { s } from "../style.js";
import { prs } from "../data.js";
import type { Task } from "../types";
import StatusPill from "../components/StatusPill";

const metricLabel = s(
  "font-size:14px; letter-spacing:0.14em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; margin-bottom:6px"
);
const metricNum = s(
  "font-family:var(--font-heading); font-weight:800; font-size:76px; line-height:0.9; letter-spacing:-0.04em"
);
const rowGrid =
  "grid-template-columns:1fr 210px 190px 150px; gap:16px; align-items:center; padding:15px 32px";

function FreshRow({ p }: { p: any }) {
  return (
    <div
      style={s(
        "display:grid; " + rowGrid + "; border-bottom:1px solid var(--color-divider)"
      )}
    >
      <div style={s("min-width:0")}>
        <div
          style={s(
            "font-size:23px; font-family:var(--font-heading); font-weight:800; letter-spacing:-0.01em; text-wrap:pretty"
          )}
        >
          {p.title}
        </div>
        <div
          style={s(
            "font-size:15px; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; letter-spacing:0.08em; text-transform:uppercase; margin-top:2px"
          )}
        >
          {p.repo}
        </div>
      </div>
      <div style={s("display:flex; align-items:center; gap:10px; min-width:0")}>
        <span
          style={s(
            "width:38px; height:38px; flex:none; background:var(--color-text); color:var(--color-bg); display:flex; align-items:center; justify-content:center; font-family:var(--font-heading); font-weight:800; font-size:14px"
          )}
        >
          {p.initials}
        </span>
        <span style={s("font-size:18px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis")}>
          {p.author}
        </span>
      </div>
      <StatusPill task={p as Task} />
      <span
        style={s(
          "font-size:22px; font-family:var(--font-heading); font-weight:800; font-variant-numeric:tabular-nums"
        )}
      >
        {p.wait}
      </span>
    </div>
  );
}

function StaleRow({ p, pulseAnim }: { p: any; pulseAnim: string }) {
  return (
    <div
      style={s(
        "display:grid; " +
          rowGrid +
          "; background:var(--color-accent); color:var(--on-accent); border-bottom:1px solid rgba(243,242,242,0.4)"
      )}
    >
      <div style={s("min-width:0")}>
        <div
          style={s(
            "font-size:23px; font-family:var(--font-heading); font-weight:800; letter-spacing:-0.01em; text-wrap:pretty"
          )}
        >
          {p.title}
        </div>
        <div
          style={s(
            "font-size:15px; opacity:0.85; font-family:var(--font-heading); font-weight:800; letter-spacing:0.08em; text-transform:uppercase; margin-top:2px"
          )}
        >
          {p.repo}
        </div>
      </div>
      <div style={s("display:flex; align-items:center; gap:10px; min-width:0")}>
        <span
          style={s(
            "width:38px; height:38px; flex:none; background:var(--on-accent); color:var(--color-accent); display:flex; align-items:center; justify-content:center; font-family:var(--font-heading); font-weight:800; font-size:14px"
          )}
        >
          {p.initials}
        </span>
        <span style={s("font-size:18px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis")}>
          {p.author}
        </span>
      </div>
      <span
        style={s(
          "display:inline-flex; align-items:center; gap:8px; padding:5px 11px; font-size:15px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; background:var(--on-accent); color:var(--color-accent)"
        )}
      >
        <span style={s("width:10px; height:10px; flex:none; background:var(--color-accent)")} />
        {p.status}
      </span>
      <span
        style={s(
          "display:inline-flex; align-items:center; gap:8px; font-size:22px; font-family:var(--font-heading); font-weight:800; font-variant-numeric:tabular-nums"
        )}
      >
        <span style={{ ...s("width:12px; height:12px; background:var(--on-accent)"), animation: pulseAnim }} />
        {p.wait}
      </span>
    </div>
  );
}

export default function ReviewPage({ pulseAnim }: { pulseAnim: string }) {
  return (
    <div style={s("flex:1; display:flex; flex-direction:column; min-height:0; overflow:hidden")}>
      <div
        style={s(
          "flex:none; display:grid; grid-template-columns:repeat(4,1fr); border-bottom:2px solid var(--color-divider)"
        )}
      >
        <div style={s("padding:20px 32px; border-right:2px solid var(--color-divider)")}>
          <div style={metricLabel}>Open PRs</div>
          <div style={metricNum}>12</div>
        </div>
        <div
          style={s(
            "padding:20px 32px; border-right:2px solid var(--color-divider); background:var(--color-accent); color:var(--on-accent)"
          )}
        >
          <div style={{ ...metricLabel, color: undefined, opacity: 0.85 }}>Over 48 hours</div>
          <div style={metricNum}>3</div>
        </div>
        <div style={s("padding:20px 32px; border-right:2px solid var(--color-divider)")}>
          <div style={metricLabel}>Median wait</div>
          <div style={metricNum}>
            9<span style={s("font-size:30px; color:var(--color-neutral-600)")}>h</span>
          </div>
        </div>
        <div style={s("padding:20px 32px")}>
          <div style={metricLabel}>Merged today</div>
          <div style={metricNum}>7</div>
        </div>
      </div>

      <section style={s("flex:1; display:flex; flex-direction:column; min-height:0; overflow:hidden")}>
        <div
          style={s(
            "flex:none; display:grid; grid-template-columns:1fr 210px 190px 150px; gap:16px; padding:10px 32px; border-bottom:2px solid var(--color-divider); font-size:13px; letter-spacing:0.12em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800"
          )}
        >
          <span>Pull request</span>
          <span>Author</span>
          <span>Review status</span>
          <span>Waiting</span>
        </div>
        <div style={s("flex:1; min-height:0; overflow:auto")}>
          {prs.map((p) =>
            p.fresh ? (
              <FreshRow key={p.title} p={p} />
            ) : (
              <StaleRow key={p.title} p={p} pulseAnim={pulseAnim} />
            )
          )}
        </div>
      </section>
    </div>
  );
}
